/**
 * SkillHero - Featured skill hero banner
 */
import { Download, Flame, Star } from 'lucide-react'
import type { Skill } from '../hooks/useSkills'

interface SkillHeroProps {
  skill: Skill
  onInstall: (id: number, e: React.MouseEvent) => void
  onClick: () => void
  installing: number | null
}

export default function SkillHero({ skill, onInstall, onClick, installing }: SkillHeroProps) {
  const color = skill.color ?? '#3b82f6'

  return (
    <div
      onClick={onClick}
      style={{
        padding: '24px', borderRadius: '14px', cursor: 'pointer',
        background: `linear-gradient(135deg, ${color}18 0%, var(--card-bg) 60%)`,
        border: `1px solid ${color}44`, position: 'relative', overflow: 'hidden',
        transition: 'all 0.15s'
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}44` }}
    >
      {/* Decorative bg circle */}
      <div style={{
        position: 'absolute', right: '-30px', top: '-30px',
        width: '120px', height: '120px', borderRadius: '50%',
        background: `${color}12`, pointerEvents: 'none'
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
        <Flame size={14} color="#f59e0b" />
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Featured Skill</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '6px' }}>{skill.name}</h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '14px', maxWidth: '480px' }}>
            {skill.description}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Star size={12} color="#f59e0b" fill="#f59e0b" /> {skill.rating.toFixed(1)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Download size={12} /> {skill.downloads.toLocaleString()} installs
            </span>
            <span style={{ padding: '2px 8px', background: `${color}18`, color, borderRadius: '4px', fontWeight: 600 }}>
              {skill.category}
            </span>
          </div>
        </div>

        <button
          onClick={e => { void onInstall(skill.id, e) }}
          disabled={skill.is_installed || installing === skill.id}
          style={{
            padding: '10px 20px', background: skill.is_installed ? '#10b981' : color,
            color: '#fff', border: 'none', borderRadius: '8px', cursor: skill.is_installed ? 'default' : 'pointer',
            fontSize: '13px', fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px',
            opacity: installing === skill.id ? 0.6 : 1
          }}
        >
          <Download size={14} />
          {skill.is_installed ? 'Installed' : installing === skill.id ? '...' : 'Install'}
        </button>
      </div>
    </div>
  )
}
